import { storage, STORAGE_KEYS } from './storage';
import type { DeviceContextConfig } from '../types';

function generateUUID(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Math.random() fallback — not cryptographically secure, but sufficient for device ID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

async function generateFingerprint(): Promise<string> {
  const signals: string[] = [];

  if (typeof navigator !== 'undefined') {
    signals.push(navigator.userAgent || '');
    signals.push(navigator.language || '');
    if ('hardwareConcurrency' in navigator) {
      signals.push(String(navigator.hardwareConcurrency));
    }
  }

  if (typeof screen !== 'undefined') {
    signals.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);
  }

  if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
    try {
      signals.push(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    } catch { /* ignore */ }
  }

  const input = signals.join('|||');

  // crypto.subtle is only available in secure contexts (HTTPS or localhost).
  // On plain HTTP it would throw a TypeError — guard and fall back to a simple hash.
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoded = new TextEncoder().encode(input);
    const buffer = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(buffer))
      .slice(0, 8)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Fallback for non-secure contexts: djb2-style hash
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

function detectPlatform(): 'web' | 'ios' | 'android' {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'web';
}

function detectOSVersion(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const ua = navigator.userAgent || '';

  const iosMatch = ua.match(/OS (\d+[._]\d+)/);
  if (iosMatch) return iosMatch[1].replace('_', '.');

  const androidMatch = ua.match(/Android (\d+[.\d]*)/);
  if (androidMatch) return androidMatch[1];

  const macMatch = ua.match(/Mac OS X (\d+[._\d]*)/);
  if (macMatch) return macMatch[1].replace(/_/g, '.');

  const winMatch = ua.match(/Windows NT (\d+[.\d]*)/);
  if (winMatch) return winMatch[1];

  return undefined;
}

function detectDeviceModel(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const ua = navigator.userAgent || '';

  const iPhoneMatch = ua.match(/iPhone (\d+,\d+)/);
  if (iPhoneMatch) return `iPhone ${iPhoneMatch[1]}`;

  const iPadMatch = ua.match(/iPad (\d+,\d+)/);
  if (iPadMatch) return `iPad ${iPadMatch[1]}`;

  const androidMatch = ua.match(/Android[\s/][\d.]+;[\s]*([^);]+)/);
  if (androidMatch) return androidMatch[1].trim();

  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux PC';

  return undefined;
}

function detectNetworkType(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const conn = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
  if (conn?.effectiveType) return conn.effectiveType;
  return undefined;
}

export class DeviceContext {
  private readonly deviceId: string;
  private readonly platform: string;
  private readonly osVersion?: string;
  private readonly deviceModel?: string;
  private readonly appVersion?: string;
  private readonly buildNumber?: string;
  private readonly isPhysicalDevice: boolean;
  private readonly networkType?: string;

  // All headers frozen after first getHeaders() call — networkType etc. will not update mid-session
  private headersCache: Record<string, string> | null = null;
  private fingerprint: string | null = null;
  private fingerprintPromise: Promise<string> | null = null;

  constructor(config?: DeviceContextConfig) {
    this.deviceId = config?.deviceId ?? storage.get(STORAGE_KEYS.DEVICE_ID) ?? generateUUID();
    storage.set(STORAGE_KEYS.DEVICE_ID, this.deviceId);

    if (config?.deviceFingerprint) {
      this.fingerprint = config.deviceFingerprint;
    }
    this.platform = config?.platform ?? detectPlatform();
    this.osVersion = config?.osVersion ?? detectOSVersion();
    this.deviceModel = config?.deviceModel ?? detectDeviceModel();
    this.appVersion = config?.appVersion;
    this.buildNumber = config?.buildNumber;
    // Mobile browsers (Android/iOS) are treated as physical devices.
    // If the backend needs to distinguish "native app" from "mobile web",
    // this flag alone is insufficient — pass an explicit `isPhysicalDevice` config.
    this.isPhysicalDevice = config?.isPhysicalDevice ?? (this.platform !== 'web');
    // networkType captured once at construction; does not update mid-session
    this.networkType = config?.networkType ?? detectNetworkType();
  }

  async getHeaders(): Promise<Record<string, string>> {
    if (this.headersCache) return this.headersCache;

    if (!this.fingerprint) {
      if (!this.fingerprintPromise) {
        this.fingerprintPromise = generateFingerprint();
      }
      this.fingerprint = await this.fingerprintPromise;
    }

    const headers: Record<string, string> = {};
    headers['X-Device-ID'] = this.deviceId;
    headers['X-Device-Fingerprint'] = this.fingerprint;
    headers['X-Platform'] = this.platform;
    if (this.osVersion) headers['X-OS-Version'] = this.osVersion;
    if (this.deviceModel) headers['X-Device-Model'] = this.deviceModel;
    if (this.appVersion) headers['X-App-Version'] = this.appVersion;
    if (this.buildNumber) headers['X-Build-Number'] = this.buildNumber;
    headers['X-Is-Physical-Device'] = String(this.isPhysicalDevice);
    if (this.networkType) headers['X-Network-Type'] = this.networkType;

    this.headersCache = headers;
    return headers;
  }
}
